import { appAxiosInstance } from '@/config/axios';

import {
	JsonValue,
	transformKeysCamelToSnakeCase,
	transformKeysSnakeToCamelCase,
} from '@/utils/transformation';

import {
	CheckReferralAccessResponse,
	IFetchActiveJobsResponse,
	IFetchInputFieldsResponse,
	IFetchMyReferralsResponse,
	IFetchSettingsResponse,
	MyReferralsResponse,
	ReferralInputFieldsResponse,
	ReferralJob,
	ReferralSettingsResponse,
	SubmitReferralPayload,
	SubmitReferralResponse,
	UpdateReferralSettingsPayload,
	UpdateReferrerFormsPayload,
} from '../types/referrals-types';

export async function fetchReferralSettings(pageId: string): Promise<ReferralSettingsResponse> {
	const response = await appAxiosInstance.post('/referral/fetch-settings', {
		page_id: pageId,
	});
	return transformKeysSnakeToCamelCase(
		response.data as IFetchSettingsResponse as unknown as JsonValue
	) as unknown as ReferralSettingsResponse;
}

export async function updateReferralSettings(
	pageId: string,
	payload: UpdateReferralSettingsPayload
): Promise<ReferralSettingsResponse> {
	const response = await appAxiosInstance.post('/referral/update-settings', {
		page_id: pageId,
		...(transformKeysCamelToSnakeCase(payload as unknown as JsonValue) as object),
	});
	return transformKeysSnakeToCamelCase(
		response.data as IFetchSettingsResponse as unknown as JsonValue
	) as unknown as ReferralSettingsResponse;
}

export async function addReferrer(
	pageId: string,
	email: string,
	allForms: boolean,
	formIdentifiers?: string[]
): Promise<void> {
	await appAxiosInstance.post('/referral/add-referrer', {
		page_id: pageId,
		...(transformKeysCamelToSnakeCase({
			email,
			allForms,
			formIdentifiers,
		} as unknown as JsonValue) as object),
	});
}

export async function updateReferrerForms(
	pageId: string,
	payload: UpdateReferrerFormsPayload
): Promise<void> {
	await appAxiosInstance.post('/referral/update-referrer-forms', {
		page_id: pageId,
		...(transformKeysCamelToSnakeCase(payload as unknown as JsonValue) as object),
	});
}

export async function removeReferrer(pageId: string, email: string): Promise<void> {
	await appAxiosInstance.post('/referral/remove-referrer', {
		page_id: pageId,
		email,
	});
}

export async function fetchReferralInputFields(
	pageId: string
): Promise<ReferralInputFieldsResponse> {
	const response = await appAxiosInstance.post('/referral/fetch-input-fields', {
		page_id: pageId,
	});
	return transformKeysSnakeToCamelCase(
		response.data as IFetchInputFieldsResponse as unknown as JsonValue
	) as unknown as ReferralInputFieldsResponse;
}

export async function fetchActiveJobs(
	pageId: string,
	toChooseForAccess?: boolean
): Promise<ReferralJob[]> {
	const response = await appAxiosInstance.post('/referral/fetch-active-jobs', {
		page_id: pageId,
		...(toChooseForAccess ? { to_choose_for_access: true } : {}),
	});
	const data = transformKeysSnakeToCamelCase(
		response.data as IFetchActiveJobsResponse as unknown as JsonValue
	) as unknown as { jobs: ReferralJob[] };
	return data.jobs ?? [];
}

export async function submitReferral(
	pageId: string,
	payload: SubmitReferralPayload
): Promise<SubmitReferralResponse> {
	const response = await appAxiosInstance.post('/referral/submit-referral', {
		page_id: pageId,
		...(transformKeysCamelToSnakeCase(payload as unknown as JsonValue) as object),
	});
	return transformKeysSnakeToCamelCase(
		response.data as JsonValue
	) as unknown as SubmitReferralResponse;
}

export async function fetchMyReferrals(
	_pageId: string,
	pageDetails: { pageSize: number; offset: number }
): Promise<MyReferralsResponse> {
	const response = await appAxiosInstance.post('/referral/fetch-my-referrals', {
		page_details: {
			page_size: pageDetails.pageSize,
			offset: pageDetails.offset,
		},
	});
	return transformKeysSnakeToCamelCase(
		response.data as IFetchMyReferralsResponse as unknown as JsonValue
	) as unknown as MyReferralsResponse;
}

export async function checkReferralAccess(handle: string): Promise<CheckReferralAccessResponse> {
	const response = await appAxiosInstance.post('/referral/check-referral-access', { handle });
	return transformKeysSnakeToCamelCase(response.data) as unknown as CheckReferralAccessResponse;
}
